/**
 * Admin: Remove Orphaned Search Documents
 *
 * Wraps the `cleanupOrphanedDocs` endpoint. GET = scan; DELETE = delete.
 * The confirmation dialog requires the user to type the orphan count.
 * See ADMIN_PORTAL_UX_REQUIREMENTS.md §5.
 */
import React, { useEffect, useState } from 'react';
import { Trash2, Search } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  scanOrphanedDocs,
  deleteOrphanedDocs,
  saveLastRun,
  loadLastRun,
  type CleanupOrphansResponse,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  ConfirmDialog,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  SampleTable,
  ButtonRow,
} from '../components/admin/AdminUI';
import { formatEventLocalDateTime } from '../utils/eventDateTime';

const ACTION = 'cleanupOrphanedDocs';

export const AdminCleanupOrphansPage: React.FC = () => {
  const { userInfo } = useAuth();
  const [scan, setScan] = useState<CleanupOrphansResponse | null>(null);
  const [scannedAt, setScannedAt] = useState<Date | null>(null);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastRun, setLastRun] = useState<LastRunRecord<CleanupOrphansResponse> | null>(null);

  useEffect(() => { setLastRun(loadLastRun(ACTION)); }, []);

  const doScan = async () => {
    setRunning(true);
    setError(null);
    setStartedAt(Date.now());
    try {
      const { data } = await scanOrphanedDocs();
      setScan(data);
      setScannedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Scan failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const doDelete = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setError(null);
    setStartedAt(Date.now());
    try {
      const { data } = await deleteOrphanedDocs();
      setScan(data); // re-render with delete results
      const record: LastRunRecord<CleanupOrphansResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: data.durationMs,
        summary: {
          deleted: data.deletedCount ?? 0,
          failed: data.failedCount ?? 0,
          orphans: data.orphanedDocuments ?? 0,
          missingDbId: data.documentsWithoutDbId ?? 0,
          totalToRemove: data.totalToRemove ?? 0,
        },
        response: data,
      };
      saveLastRun(ACTION, record);
      setLastRun(record);
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  // The backend now returns BOTH sample arrays and a combined `totalToRemove`
  // count. Older builds returned only `sampleOrphans`; we fall back to that
  // for backward compatibility.
  const orphanCount = scan?.orphanedDocuments ?? 0;
  const missingDbIdCount = scan?.documentsWithoutDbId ?? 0;
  const totalToRemove = scan?.totalToRemove ?? (orphanCount + missingDbIdCount);
  const sampleOrphans = scan?.sampleOrphanedDocs ?? scan?.sampleOrphans ?? [];
  const sampleMissingDbId = scan?.sampleDocsWithoutDbId ?? [];
  const indexCapped = scan?.totalSearchDocuments === 1000;

  return (
    <AdminLayout
      currentPage="cleanupOrphans"
      pageTitle="Remove Orphaned Search Documents"
      pageDescription="Find search index documents whose database row no longer exists, and delete them."
    >
      <Card>
        <CardTitle>Scan</CardTitle>
        <CardDescription>
          Compares search index documents against the database. Read-only —
          deleting requires a separate confirmation.
        </CardDescription>
        <ButtonRow>
          <PrimaryButton onClick={doScan} disabled={running}>
            <Search size={16} />
            Scan for orphans
          </PrimaryButton>
          {scannedAt && !running && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Last scan: {scannedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
        {running && startedAt != null && (
          <RunningIndicator startedAt={startedAt} message="Scanning the search index…" />
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </Card>

      {scan && (
        <Card>
          <CardTitle>Scan results</CardTitle>
          {indexCapped && (
            <Banner tone="warning">
              ⚠ Only the first 1,000 search documents were scanned. For an
              authoritative count, run <strong>Search Index Health</strong> first.
            </Banner>
          )}

          <StatGrid>
            <Metric label="Search docs scanned" value={scan.totalSearchDocuments.toLocaleString()} />
            <Metric label="Valid DB IDs" value={scan.validDatabaseIds.toLocaleString()} />
            <Metric
              label="Stale orphans"
              value={orphanCount.toLocaleString()}
              hint="DB ID no longer exists"
              tone={orphanCount > 0 ? 'warning' : 'success'}
            />
            <Metric
              label="Missing DB ID"
              value={missingDbIdCount.toLocaleString()}
              hint="No databaseEventId field"
              tone={missingDbIdCount > 0 ? 'warning' : 'success'}
            />
            <Metric
              label="Total to remove"
              value={totalToRemove.toLocaleString()}
              tone={totalToRemove > 0 ? 'warning' : 'success'}
            />
            {scan.dryRun != null && (
              <Metric
                label="Mode"
                value={scan.dryRun ? 'Dry run (scan)' : 'Delete'}
                tone={scan.dryRun ? 'neutral' : 'success'}
              />
            )}
            {scan.deletedCount != null && (
              <Metric label="Deleted" value={scan.deletedCount.toLocaleString()} tone="success" />
            )}
            {scan.failedCount != null && scan.failedCount > 0 && (
              <Metric label="Failed" value={scan.failedCount.toLocaleString()} tone="error" />
            )}
          </StatGrid>

          {scan.message && <Banner tone="info">{scan.message}</Banner>}

          {sampleOrphans.length > 0 && (
            <>
              <CardTitle style={{ fontSize: 16, marginTop: 16 }}>
                Sample stale orphans ({sampleOrphans.length})
              </CardTitle>
              <CardDescription>
                Search documents whose <code>databaseEventId</code> no longer
                exists in the database.
              </CardDescription>
              <SampleTable>
                <thead>
                  <tr>
                    <th>SearchId</th>
                    <th>DB Id</th>
                    <th>Title</th>
                    <th>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleOrphans.slice(0, 10).map((d) => (
                    <tr key={d.searchId}>
                      <td>{d.searchId}</td>
                      <td>{d.databaseEventId ?? '—'}</td>
                      <td>{d.title ?? '—'}</td>
                      <td>{formatEventLocalDateTime(d.startDate) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}

          {sampleMissingDbId.length > 0 && (
            <>
              <CardTitle style={{ fontSize: 16, marginTop: 16 }}>
                Sample docs missing DB ID ({sampleMissingDbId.length})
              </CardTitle>
              <CardDescription>
                Search documents that have no <code>databaseEventId</code>{' '}
                field at all — these can't be reconciled to a database row.
              </CardDescription>
              <SampleTable>
                <thead>
                  <tr>
                    <th>SearchId</th>
                    <th>Title</th>
                    <th>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleMissingDbId.slice(0, 10).map((d) => (
                    <tr key={d.searchId}>
                      <td>{d.searchId}</td>
                      <td>{d.title ?? '—'}</td>
                      <td>{formatEventLocalDateTime(d.startDate) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}

          {scan.errors && scan.errors.length > 0 && (
            <>
              <CardTitle style={{ fontSize: 16, marginTop: 16, color: '#cf1322' }}>
                Errors
              </CardTitle>
              <SampleTable>
                <thead><tr><th>#</th><th>Message</th></tr></thead>
                <tbody>
                  {scan.errors.map((e, i) => (
                    <tr key={i}><td>{i + 1}</td><td>{e.message}</td></tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}

          <ButtonRow>
            <PrimaryButton
              onClick={() => setConfirmOpen(true)}
              disabled={totalToRemove === 0 || running}
              style={{ background: totalToRemove > 0 ? '#cf1322' : undefined }}
            >
              <Trash2 size={16} />
              Delete {totalToRemove} document{totalToRemove === 1 ? '' : 's'}
            </PrimaryButton>
            <SecondaryButton onClick={doScan} disabled={running}>
              <Search size={16} />
              Scan again
            </SecondaryButton>
          </ButtonRow>

          <RawJson data={scan} />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete orphaned documents"
        body={
          <>
            <p>
              You're about to delete <strong>{totalToRemove}</strong> search
              document{totalToRemove === 1 ? '' : 's'}:
            </p>
            <ul style={{ marginTop: 8, marginBottom: 12 }}>
              <li>
                <strong>{orphanCount}</strong> stale orphan
                {orphanCount === 1 ? '' : 's'} (database row no longer exists)
              </li>
              <li>
                <strong>{missingDbIdCount}</strong> document
                {missingDbIdCount === 1 ? '' : 's'} with no{' '}
                <code>databaseEventId</code> field
              </li>
            </ul>
            <p>This cannot be undone.</p>
            <p>
              The originating database rows are already gone (or were never
              linked), so no data is lost — but documents will need to be
              re-indexed if they were deleted in error.
            </p>
          </>
        }
        promptText={<>Type <code>{totalToRemove}</code> (the total to remove) to confirm:</>}
        confirmPhrase={String(totalToRemove)}
        primaryLabel={`Delete ${totalToRemove} document${totalToRemove === 1 ? '' : 's'}`}
        onConfirm={doDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminCleanupOrphansPage;
